import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];

export default function Shop() {
    const [params, setParams] = useSearchParams();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [saleBanner, setSaleBanner] = useState(null);
    const [currentCategory, setCurrentCategory] = useState(null);
    const [loading, setLoading] = useState(true);

    const gender = params.get("gender") || "all";
    const category = params.get("category") || "";
    const size = params.get("size") || "";
    const min_price = params.get("min_price") || "";
    const max_price = params.get("max_price") || "";
    const saleId = params.get("sale") || "";

    useEffect(() => { api.get("/categories").then(r => setCategories(r.data)); }, []);

    useEffect(() => {
        if (category) {
            api.get(`/categories/${category}`).then(r => setCurrentCategory(r.data)).catch(() => setCurrentCategory(null));
        } else {
            setCurrentCategory(null);
        }
    }, [category]);

    useEffect(() => {
        setLoading(true);
        const fetch = async () => {
            if (saleId) {
                const r = await api.get("/sale/active");
                setSaleBanner(r.data);
                setProducts(r.data?.products || []);
                setLoading(false);
                return;
            }
            setSaleBanner(null);
            const q = { limit: 60 };
            if (gender && gender !== "all") q.gender = gender;
            if (category) q.category = category;
            if (size) q.size = size;
            if (min_price) q.min_price = min_price;
            if (max_price) q.max_price = max_price;
            const r = await api.get("/products", { params: q });
            setProducts(r.data);
            setLoading(false);
        };
        fetch();
    }, [gender, category, size, min_price, max_price, saleId]);

    const setParam = (k, v) => {
        const p = new URLSearchParams(params);
        if (v) p.set(k, v); else p.delete(k);
        p.delete("sale");
        setParams(p);
    };

    return (
        <div className="max-w-[1440px] mx-auto px-4 md:px-10 py-10" data-testid="shop-page">
            {currentCategory?.banner_image_url && (
                <div className="mb-8 relative aspect-[3/1] border-2 border-black overflow-hidden">
                    <img src={currentCategory.banner_image_url} alt={currentCategory.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40 flex flex-col justify-end p-6 md:p-10 text-white">
                        <div className="font-mono text-[10px] uppercase">Category</div>
                        <div className="font-display text-4xl md:text-6xl tracking-tighter">{currentCategory.name}</div>
                        {currentCategory.description && <div className="font-mono text-sm mt-2 max-w-xl">{currentCategory.description}</div>}
                    </div>
                </div>
            )}
            <div className="mb-8">
                <div className="font-mono text-[10px] uppercase text-neutral-500">Catalogue</div>
                <h1 className="font-display text-5xl md:text-7xl tracking-tighter">{saleBanner ? saleBanner.name : (currentCategory?.name || "Shop")}</h1>
                {saleBanner && <p className="font-mono text-xs uppercase mt-2" style={{ color: "hsl(var(--accent))" }}>{saleBanner.headline} — up to {saleBanner.discount_percent}% off</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-8">
                <aside className="border-2 border-black p-6 h-fit md:sticky md:top-24" data-testid="filter-sidebar">
                    <div className="font-mono text-xs uppercase tracking-widest mb-4">Filters</div>

                    <div className="mb-6">
                        <div className="font-mono text-[10px] uppercase mb-2 text-neutral-500">Gender</div>
                        <div className="flex flex-wrap gap-2">
                            {["all","men","women","unisex"].map(g => (
                                <button key={g} onClick={() => setParam("gender", g === "all" ? "" : g)} data-testid={`filter-gender-${g}`} className={`px-3 py-1 border-2 border-black font-mono text-[10px] uppercase ${gender === g ? "bg-black text-white" : "bg-white"}`}>{g}</button>
                            ))}
                        </div>
                    </div>

                    <div className="mb-6">
                        <div className="font-mono text-[10px] uppercase mb-2 text-neutral-500">Category</div>
                        <div className="flex flex-col gap-2">
                            <button onClick={() => setParam("category", "")} className={`text-left font-mono text-xs uppercase ${!category ? "underline" : ""}`}>All</button>
                            {categories.map(c => (
                                <button key={c.id} onClick={() => setParam("category", c.slug)} data-testid={`filter-cat-${c.slug}`} className={`text-left font-mono text-xs uppercase ${category === c.slug ? "underline" : ""}`}>{c.name}</button>
                            ))}
                        </div>
                    </div>

                    <div className="mb-6">
                        <div className="font-mono text-[10px] uppercase mb-2 text-neutral-500">Size</div>
                        <div className="flex flex-wrap gap-2">
                            {SIZES.map(s => (
                                <button key={s} onClick={() => setParam("size", size === s ? "" : s)} data-testid={`filter-size-${s}`} className={`px-3 py-1 border-2 border-black font-mono text-[10px] ${size === s ? "bg-black text-white" : "bg-white"}`}>{s}</button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <div className="font-mono text-[10px] uppercase mb-2 text-neutral-500">Price (INR)</div>
                        <div className="flex gap-2">
                            <input type="number" placeholder="Min" defaultValue={min_price} onBlur={e => setParam("min_price", e.target.value)} className="w-full border-2 border-black p-2 font-mono text-xs" data-testid="filter-min-price" />
                            <input type="number" placeholder="Max" defaultValue={max_price} onBlur={e => setParam("max_price", e.target.value)} className="w-full border-2 border-black p-2 font-mono text-xs" data-testid="filter-max-price" />
                        </div>
                    </div>
                </aside>

                <div>
                    <div className="mb-4 font-mono text-xs uppercase text-neutral-500" data-testid="results-count">{loading ? "Loading…" : `${products.length} products`}</div>
                    {!loading && products.length === 0 ? (
                        <div className="border-2 border-black p-16 text-center font-mono text-sm" data-testid="no-products">No products match. Try changing filters.</div>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                            {products.map(p => <ProductCard key={p.id} product={p} />)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
